// src/components/common/Header.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { motion } from 'framer-motion';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, currentUser, userRole, logout } = useAuth();
  const { companyInfo } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Track scroll position to add shadow when scrolled
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const handleLogoClick = () => {
    navigate('/dashboard');
  };
  
  const handleCreateQuotation = () => {
    navigate('/quotation/new');
  };
  
  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };
  
  // Don't show header on login page
  if (!isAuthenticated || location.pathname === '/login') {
    return null;
  }
  
  return (
    <motion.header 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className={`bg-white fixed top-0 left-0 right-0 z-50 border-b border-gray-200 ${
        scrolled ? 'shadow-sm' : ''
      } transition-all duration-200`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Company Name */}
          <motion.div 
            className="flex items-center cursor-pointer group flex-shrink-0"
            onClick={handleLogoClick}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex-shrink-0 bg-[#FDE8E9] p-2 rounded-xl overflow-hidden border border-[#F9B3B7] shadow-sm">
              <img 
                src={companyInfo.favicon || companyInfo.logo || "/apex-logo-placeholder.png"}
                alt={companyInfo.name || "Company Logo"} 
                className="h-8 lg:h-9 w-auto object-contain"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iIzNiNDJjYyIvPjxwYXRoIGQ9Ik0zMCA3MEw1MCAzMEw3MCA3MFoiIGZpbGw9IiNmZmZmZmYiLz48L3N2Zz4=';
                }} 
              />
            </div>
            <div className="ml-3 lg:ml-4 flex flex-col">
              <span className="text-base lg:text-lg font-bold text-gray-900 hidden sm:block truncate max-w-[200px] lg:max-w-none">
                {companyInfo.name || "Apex Interio"}
              </span>
              <span className="text-xs text-gray-500 hidden sm:block">
                Quotation System
              </span>
            </div>
          </motion.div>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6">
            <motion.button
              onClick={handleCreateQuotation}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-semibold rounded-lg text-white bg-[#E30613] hover:bg-[#C70511] focus:outline-none transition-all duration-200"
            >
              <svg 
                className="h-4 w-4 mr-2" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6" 
                />
              </svg>
              Create Quotation
            </motion.button>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-800">{currentUser?.email}</div>
                <div className="text-xs text-gray-500 flex items-center">
                  <div className={`w-2 h-2 rounded-full mr-2 ${
                    userRole?.role === 'admin' ? 'bg-green-500' : 'bg-blue-500'
                  }`}></div>
                  {userRole?.role === 'admin' ? 'Administrator' : 'Staff Member'}
                </div>
              </div>

              <motion.button
                onClick={handleLogout}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center px-4 py-2 border border-gray-200 text-sm font-medium rounded-lg text-gray-600 bg-white hover:bg-gray-50 hover:border-gray-300 focus:outline-none transition-all duration-200"
              >
                <svg 
                  className="h-4 w-4 mr-2 text-gray-400" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" 
                  />
                </svg>
                Logout
              </motion.button>
            </div>
          </div>
          
          {/* Mobile Navigation */}
          <div className="lg:hidden flex items-center space-x-3">
            {/* Mobile Create Quotation Button */}
              <motion.button
                onClick={handleCreateQuotation}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center p-3 border border-transparent text-sm font-semibold rounded-lg text-white bg-[#E30613] hover:bg-[#C70511] focus:outline-none transition-all duration-200"
                title="Create Quotation"
              >
                <svg 
                  className="h-5 w-5" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6" 
                  />
                </svg>
              </motion.button>
              
            {/* Mobile Menu Toggle */}
              <motion.button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-3 rounded-xl text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#E30613] transition-all duration-200"
                aria-label="Toggle mobile menu"
              >
                {mobileMenuOpen ? (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </motion.button>
          </div>
        </div>
      </div>
      
      {/* Mobile menu, show/hide based on menu state */}
      {mobileMenuOpen && (
        <motion.div 
          className="lg:hidden bg-white border-t border-gray-200 shadow-sm"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="px-4 pt-4 pb-4 space-y-4">
            {/* User Info Section */}
            <div className="flex justify-between items-center py-4 border-b border-gray-100/50">
              <div className="text-sm text-gray-500">Signed in as:</div>
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-800 truncate max-w-[180px]">{currentUser?.email}</div>
                <div className="text-xs text-gray-500 flex items-center justify-end">
                  <div className={`w-2 h-2 rounded-full mr-2 ${
                    userRole?.role === 'admin' ? 'bg-green-500' : 'bg-blue-500'
                  }`}></div>
                  {userRole?.role === 'admin' ? 'Administrator' : 'Staff Member'}
                </div>
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="py-2">
              <button
                onClick={() => {
                  handleCreateQuotation();
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center justify-center px-6 py-3 bg-[#E30613] text-white rounded-lg hover:bg-[#C70511] focus:outline-none transition-all duration-200 font-semibold"
              >
                <svg 
                  className="h-5 w-5 mr-2" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6" 
                  />
                </svg>
                Create New Quotation
              </button>
            </div>
            
            {/* Logout Button */}
            <div className="pt-2">
            <button
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center justify-center px-6 py-3 border border-gray-200 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 focus:outline-none transition-all duration-200"
            >
              <svg 
                className="h-4 w-4 mr-2 text-gray-400" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" 
                />
              </svg>
              Sign Out
            </button>
            </div>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};

export default Header;